import {
  B1,
  BackgroundIcon,
  CardPage,
  T1,
  AppContext,
  DATE_FORMAT_FOR_DATE_PICKER,
  TICKET_SOURCE,
  useCheckFeatureGating,
  fetchApi,
  DATE_FORMAT_MMM_DD_YYYY,
  TIME_FORMAT_HH_MM_A,
  reactSelectInlineError,
  DATE_TIME_FORMAT,
  DATE_FORMAT,
  COMMON_CONSTANTS,
  FEATURE_GATING_STATUS
} from '@nile/components';
import { useLazyQuery } from '@apollo/client';
import envConfig from '@nile/home/src/Config/env';
import AssetTypeCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-type-cell';
import { SVGIcon } from '@superops/super-icons';
import { Link, AppProvider, Banner, Card } from '@superops/ui-design-system';
import dayjs from 'dayjs';
import produce from 'immer';
import { includes, isEqual, get, cloneDeep, isEmpty, omit } from 'lodash';
import React, { useEffect, useRef, useState, useContext } from 'react';
import {
  FormattedMessage,
  IntlProvider,
  createIntl,
  injectIntl,
  useIntl as useReactIntl
} from 'react-intl';
import { Slide, toast, Zoom } from 'react-toastify';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from 'styled-components';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { datadogRum } from '@datadog/browser-rum';
import {
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  getClientSiteVariablesById,
  SAVE_PERSONALIZATION
} from '@nile/graphql';
import {
  GET_CLIENT_SITE_LIST_FOR_DROPDOWN,
  GET_CLIENT_SITE_LIST_WITH_INSTALLER_INFO,
  GET_CLIENT_SITE_LIST_FOR_DROPDOWN_WO_ACCOUNT
} from '@nile/banyan/src/Graphql/Mutations/site';
import { GET_NOTIFICATION_TEMPLATE_BY_CONSTANT } from '@nile/banyan/src/Graphql/Mutations/msp';
import { getDimensions } from '@nile/reports/src/modules/charts/utils/layoutHelper';
import nanoid from 'nanoid';
import dompurify from 'dompurify';
import BigNumber from 'bignumber.js';
import {
  GET_AI_FEATURES_LIST,
  GET_AI_FEATURE_GROUPS
} from '@nile/banyan/src/Graphql/Query/ai';
import markdownit from 'markdown-it';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';

const permissionMap = {
  add: '1',
  edit: '2',
  delete: '3',
  view: '4',
  allow: '5'
};
const TRASH_SYSTEM_VIEW_PROPERTY = ['TRASH_ASSET'];
const trashIcon = { type: 'supericon', name: 'MoveTrash' };

function getTranslatedText(intl, key, values) {
  return intl.formatMessage({ id: key }, values);
}

function getURLSearchParams() {
  const hash = window.location.hash;
  const queryString = hash.split('?')[1];
  const params = new URLSearchParams(queryString);
  return params;
}

function dompurifier(content, config = {}) {
  return dompurify.sanitize(content, {
    ADD_ATTR: ['target', 'contenteditable'],
    ...config
  });
}

function getDisplayName(data) {
  const displayVal =
    data?.firstName && data?.lastName
      ? `${data.firstName} ${data.lastName}`
      : data?.firstName
      ? data?.firstName
      : data?.name
      ? data?.name
      : '';

  return displayVal;
}

function getParsedCrudData(base, current, keyToCompare = 'id') {
  const addedItems = current.filter(
    (el) => !base.some((t) => get(t, keyToCompare) === get(el, keyToCompare))
  );
  const deletedItems = base.filter(
    (el) => !current.some((i) => get(i, keyToCompare) === get(el, keyToCompare))
  );
  const updatedItems = current.filter((el) => {
    const index = base.findIndex(
      (t) => get(t, keyToCompare) === get(el, keyToCompare)
    );
    if (index > -1) {
      if (!isEqual(el, base[index])) {
        return true;
      }
    }
    return false;
  });
  return {
    addedItems,
    deletedItems,
    updatedItems
  };
}
function isProductAuthorised(currentProduct, productToCheck) {
  // incase in future if we need to check for multiple products, pass the products key in array format
  if (typeof productToCheck === 'object') {
    return productToCheck.some((p) => p.includes(currentProduct));
  }
  return currentProduct === productToCheck;
}

function isAuthorisedRole(reference, role) {
  const rolePath = role?.split('.');
  const permissionToCheck = rolePath[rolePath.length - 1];
  const permissionId = permissionMap[permissionToCheck];
  const feature = reference.find(
    (el) => el.feature && el.feature.key === rolePath[0]
  );
  if (!feature) return false;
  const isFeatureAuthorised = feature.operationList?.find(
    (el) => el.operationId === permissionId
  );
  return !!isFeatureAuthorised;
}

function isFeatureAuthorisedInPlan(featureName, options = {}) {
  const appContext = useContext(AppContext);
  const featureObj = appContext?.planFeatureList?.[featureName];
  const { isTrialEligible = false, isDataCleanupRequired = false } = options;

  if (featureObj) {
    const status = featureObj.status;
    let enabled =
      status === FEATURE_GATING_STATUS.ENABLED ||
      status === FEATURE_GATING_STATUS.IN_TRIAL;

    if (isTrialEligible && status === FEATURE_GATING_STATUS.TRIAL_ELIGIBLE) {
      enabled = true;
    }

    if (isDataCleanupRequired && status === FEATURE_GATING_STATUS.EXPIRED) {
      enabled = true;
    }

    return enabled || featureObj.upsell;
  }
  return false;
}

function isAuthorised(reference, roles, featureGateName = null) {
  if (featureGateName && !useCheckFeatureGating(featureGateName)?.enabled) {
    return false;
  }

  if (typeof roles === 'string') {
    return isAuthorisedRole(reference, roles);
  }

  if (typeof roles === 'object') {
    const { operator, roles: rolesToAuthorize } = roles;
    switch (operator) {
      case 'OR':
        return rolesToAuthorize.reduce(
          (acc, role) => acc || isAuthorisedRole(reference, role),
          false
        );

      case 'AND':
      default:
        return rolesToAuthorize.reduce(
          (acc, role) => acc && isAuthorisedRole(reference, role),
          true
        );
    }
  }

  return false;
}

function sendMessageToContentJS(eventname) {
  const msgEvent = new Event(eventname);
  try {
    window.dispatchEvent(msgEvent);
  } catch {
    console.error('Extension not available');
  }
}

const EventEmitter = {
  events: {},
  dispatch(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach((callback) => callback(data));
  },
  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
  },
  unsubscribe(event) {
    if (!this.events[event]) return;
    delete this.events[event];
  }
};

const EventEmitterWithIndex = {
  events: {},
  dispatch(event, data) {
    if (!this.events[event]) return;
    Object.values(this.events[event]).forEach((callback) => callback(data));
  },
  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = {};
    const uniqName = generateString('5');
    this.events[event] = {
      ...this.events[event],
      [uniqName]: callback
    };
    // this.events[event].push(callback);
    return uniqName;
  },
  unsubscribe(event, uniqName) {
    if (!this.events[event] || !this.events[event]?.[uniqName]) return;
    delete this.events[event][uniqName];
  }
};

const getErrorTexts = (errorText, value) => {
  switch (errorText) {
    case 'currentAndNewPasswordSame':
      return <FormattedMessage id="app.auth.currentAndNewPasswordSame" />;
    case 'passwordMismatch':
      return <FormattedMessage id="app.auth.passwordMismatch" />;
    case 'emptyPassword':
      return <FormattedMessage id="app.auth.emptyPassword" />;
    case 'passwordLengthGreaterThan100':
      return <FormattedMessage id="app.auth.passwordLengthGreaterThan100" />;
    case 'passwordLengthGreaterThanX':
      return (
        <>
          <FormattedMessage id="app.auth.passwordLengthGreaterThanX" />
          {` ${value} `}
          <FormattedMessage id="app.auth.characters" />
        </>
      );
    case 'passwordNoAlphaNumeric':
      return <FormattedMessage id="app.auth.passwordNoAlphaNumeric" />;
    case 'passwordNoMixcase':
      return <FormattedMessage id="app.auth.passwordNoMixcase" />;
    case 'passwordNoSpecialChar':
      return <FormattedMessage id="app.auth.passwordNoSpecialChar" />;
    case 'passwordEmailNotAllowed':
      return <FormattedMessage id="app.auth.passwordEmailNotAllowed" />;
    case 'passwordNoPrevXPassword':
      return (
        <>
          <FormattedMessage id="app.auth.passwordNoPrevXPassword" />
          {` ${value} `}
          <FormattedMessage id="app.auth.passwords" />
        </>
      );
    default:
      return <FormattedMessage id="app.auth.errorOccured" />;
  }
};

const getErrorCode = (errorCode, params) => {
  switch (errorCode) {
    case 558:
      return (
        <div>
          <FormattedMessage id="app.auth.userNamePasswordMismatch" />
        </div>
      );
    case 559:
      return (
        <div>
          <FormattedMessage id="app.auth.userAccountLocked" />
        </div>
      );
    case 560:
      return (
        <div>
          <FormattedMessage id="app.auth.userAccountNotActivated" />
        </div>
      );
    case 563:
      return (
        <div>
          <FormattedMessage id="app.errorCode.563" />
        </div>
      );
    case 557:
      return (
        <div>
          <FormattedMessage id="app.auth.noUserFound" />
        </div>
      );
    case 556:
      return (
        <div>
          <FormattedMessage id="app.auth.invalidToken" />
        </div>
      );
    case 554:
      let detailedError = '';
      if (params) {
        return (
          <div>
            {/* <div>
              <B1>Password update failed</B1>
            </div> */}
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {Object.keys(params).map((param) => (
                <div>{getErrorTexts(param, params[param])}</div>
              ))}
            </div>
          </div>
        );
      }
      return (
        <div>
          <FormattedMessage id="app.auth.pwdUpdateFailed" />
        </div>
      );
    case 555:
      let errorDetails = '';
      if (params) {
        return (
          <div>
            {/* <div>
              <B1>Password does not fullfill policy requirements</B1>
            </div> */}
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {Object.keys(params).map((param) => (
                <div>{getErrorTexts(param, params[param])}</div>
              ))}
            </div>
          </div>
        );
      }
      return (
        <div>
          <FormattedMessage id="app.auth.pwdFullfillReq" />
        </div>
      );
    case 569:
      return (
        <div>
          <FormattedMessage id="forgot.password.error.ssoEnabled" />
        </div>
      );
    default:
      return (
        <div>
          <FormattedMessage id="app.auth.errorOccured" />
        </div>
      );
  }
};

const getErrorData = (
  errorCode,
  params,
  errorMessage = <FormattedMessage id="common.error.occured" />
) => {
  return (
    <Banner title={errorMessage} status="critical">
      {getErrorCode(errorCode, params)}
    </Banner>
  );
};

const getFormattedDateTime = (dateTime, dateFormat, timeFormat) => {
  if (timeFormat && dateFormat) {
    return dayjs(dateTime).format(
      `${dateFormat || 'MMM D, YYYY h:mm A'} ${timeFormat}`
    );
  }
  if (timeFormat) {
    return dayjs(dateTime).format(`${timeFormat}`);
  }
  return dayjs(dateTime).format(dateFormat || 'MMM D, YYYY h:mm A');
};

const iconMap = {
  success: 'Success',
  error: 'Error',
  warning: 'WarningToast',
  info: 'alert-info'
};

const ToastWrapper = styled.div`
  .outer-container {
    display: flex;
    align-items: center;
  }
  .detail-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1 1;
    padding-right: 30px;
    .title {
      font-size: 16px;
      line-height: 1.5;
    }
    .description {
      font-size: 12px;
      color: black;
    }
  }
`;

const AnimatedToastWrapper = styled.div`
  .outer-container {
    display: flex;
    align-items: center;
    .animated-image {
      width: 100%;
    }
  }
  .detail-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1 1;
    .title {
      color: #0f0f0f;
      font-size: 20px;
      line-height: 1.5;
    }
    .description {
      font-size: 12px;
      color: black;
    }
  }
`;

const getToastMessage = (
  type = 'success',
  message = 'Successful',
  lang = 'en',
  fields
) => {
  return (
    <IntlProvider
      onError={() => {}}
      locale={lang?.replace(/_/g, '-')}
      messages={window.TranslationMessages}
    >
      <ToastWrapper className="flex">
        <div className="outer-container">
          <BackgroundIcon
            url={`url(${envConfig.imageRoot}svg/${iconMap[type]}.svg)`}
            height="30px"
            width="30px"
            style={{ marginRight: 10 }}
          />
        </div>
        <div className="detail-wrapper">
          <div className="description">
            <T1>
              <FormattedMessage id={message} values={fields} />
            </T1>
          </div>
        </div>
      </ToastWrapper>
    </IntlProvider>
  );
};

const getActionableToast = (
  type = 'success',
  message = 'Click to Action',
  primaryAction,
  lang = 'en',
  values
) => {
  return (
    <IntlProvider
      onError={() => {}}
      locale={lang?.replace(/_/g, '-')}
      messages={window.TranslationMessages}
    >
      <AppProvider>
        <ToastWrapper className="flex">
          <div className="flex actr full-width mr-10">
            <div className="mr8 fx actr">
              <BackgroundIcon
                url={`url(${envConfig.imageRoot}svg/${iconMap[type]}.svg)`}
                height="30px"
                width="30px"
              />
            </div>
            <div className="title">
              <div className="description">
                <T1>
                  <FormattedMessage id={message} values={values} />
                </T1>
              </div>
            </div>
            <div className="ml8">
              <Link onClick={primaryAction.action} removeUnderline>
                <FormattedMessage id={primaryAction.title} />
              </Link>
            </div>
            {message.includes('2112') ? (
              <div className="ml8">
                <Link onClick={() => window.Intercom('show')} removeUnderline>
                  <FormattedMessage id="app.pricing.contactsupport" />
                </Link>
              </div>
            ) : (
              ''
            )}
          </div>
        </ToastWrapper>
      </AppProvider>
    </IntlProvider>
  );
};

const getAnimatedToastMessage = (title, description, icon, lang) => {
  return (
    <IntlProvider
      onError={() => {}}
      locale={lang?.replace(/_/g, '-')}
      messages={TranslationMessages[lang]}
    >
      <AnimatedToastWrapper>
        <div className="outer-container">
          <img
            className="animated-image"
            src={`${envConfig.imageRoot}gif/${icon}`}
            alt="success toast"
          />
        </div>
        <div className="detail-wrapper">
          <div className="title">
            <B1>{title}</B1>
          </div>
          <div className="description">
            <T1>{description}</T1>
          </div>
        </div>
      </AnimatedToastWrapper>
    </IntlProvider>
  );
};

const getToastConfig = (type = 'success', overriddenProperties = {}) => {
  const toastStyleMap = {
    success: {
      position: 'bottom-center',
      autoClose: 3000,
      hideProgressBar: false,
      toastId: 'Sops-success-toast',
      pauseOnHover: true,
      closeOnClick: true,
      className: 'br-5 full-width Sops-notify Sops-notify-success',
      transition: Slide,
      ...overriddenProperties
    },
    error: {
      position: 'bottom-center',
      autoClose: 7000,
      toastId: 'Sops-error-toast',
      hideProgressBar: false,
      pauseOnHover: true,
      closeOnClick: true,
      className: 'br-5 full-width Sops-notify Sops-notify-error',
      transition: Slide,
      ...overriddenProperties
    },
    warning: {
      position: 'bottom-center',
      autoClose: 5000,
      toastId: 'Sops-warning-toast',
      hideProgressBar: false,
      pauseOnHover: true,
      closeOnClick: true,
      className: 'br-5 full-width Sops-notify Sops-notify-warning',
      transition: Slide,
      ...overriddenProperties
    },
    info: {
      position: 'bottom-center',
      autoClose: 5000,
      toastId: 'Sops-info-toast',
      hideProgressBar: false,
      pauseOnHover: true,
      closeOnClick: true,
      className: 'br-5 full-width Sops-notify Sops-notify-info',
      transition: Slide,
      ...overriddenProperties
    }
  };
  return toastStyleMap[type];
};

const toastMessage = (
  type = 'success',
  message = '-',
  lang = 'en',
  fields,
  overriddenProperties
) => {
  return toast(
    getToastMessage(type, message, lang, fields),
    getToastConfig(type, overriddenProperties)
  );
};

const actionToastMessage = (
  type = 'success',
  message = '-',
  primaryAction = {
    title: 'Icon',
    action: () => console.log('clicked')
  },
  lang = 'en',
  overriddenProperties,
  values
) => {
  return toast(
    getActionableToast(type, message, primaryAction, lang, values),
    getToastConfig(type, overriddenProperties)
  );
};

const animatedToast = (
  title = 'Successful',
  description = 'Operation successful',
  icon = 'animatedmessage.gif',
  lang = 'en'
) => {
  return toast(getAnimatedToastMessage(title, description, icon, lang), {
    position: 'bottom-center',
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    className: 'animated-toaster',
    transition: Zoom
  });
};

const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

function generateString(length) {
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

function planName() {
  const flags = useFlags();
  // if (flags?.connectPlan) {
  return {
    planNameConnect: 'connect',
    planNameStandardRmm: 'standard-rmm',
    planNameGrow: 'grow',
    planNameStandardPsa: 'standard-psa'
  };
  // }
}

const getCookieValue = (name) => {
  return document.cookie
    .match(RegExp(name + '=.[^;]*'))
    ?.pop()
    ?.replace(`${name}=`, '');
};

const allowedDomains = [
  'superops',
  'superopsbeta',
  'superopsalpha',
  'superopsmsp'
];

//Checking for other domain except superops, if exists setting domain name as CustomerSubDomain
const getCustomSubDomain = () => {
  if (allowedDomains.includes(window.location.hostname.split('.')[1])) {
    return envConfig.mode === 'development' ||
      envConfig.mode === 'frontend' ||
      localStorage.getItem('frontend')
      ? localStorage.getItem('CustomerSubDomain')
      : window.location.hostname.split('.')[0];
  } else {
    return window.location.hostname;
  }
};

const getHeaderValue = () => {
  const windowId = sessionStorage.getItem('windowId');
  return {
    CustomerSubDomain: getCustomSubDomain(),
    'X-CSRF-TOKEN': getCookieValue('app-csrf'),
    'X-SO-UA-Build-Version': '1.8',
    'X-SO-UA-Window-ID': windowId
  };
};

const getIdpHeaders = () => {
  const appHeaders = getHeaderValue();
  const isDevMode = envConfig.mode === 'development';
  return {
    ...appHeaders,
    'Content-Type': 'application/json',
    Authorization: 'Bearer tv0k9XYSWqCux1JFt1DZb5f1KQkVruYA',
    ...(isDevMode
      ? { CustomOrigin: `${appHeaders.CustomerSubDomain}.superopsalpha.com` }
      : {})
  };
};

const getEntityAttributes = (entityFields, module) => {
  return (
    entityFields?.getEntitiesMeta.find((el) => el.name === module)
      ?.attributes || []
  );
};

/**
 * Helper to find the last index of an object occurence in an array of objects.
 * const options = [{value: 'vignesh', name: 'pvs'}, {value: 'subramaniam', name: 'pvs'}, {value: 'vignesh': name:'pvs'}]
 * findLastIndex(options, 'name', ['pvs']) will return 2
 *
 * @param {array} array
 * @param {String} keyToCompare
 * @param {array} keys
 */
const findLastIndex = (array = [], keyToCompare, keys) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (includes(keys, array[i][keyToCompare])) return i;
  }
  return -1;
};

/**
 * Helper to format the value for react select dropdown.
 * const options = [{id: '1', name: 'one'}]
 *
 * formatDataForSelectDropdown(options) will return
 * [{id: '1', name: 'one', value: 'one', label: 'one'}]
 *
 * const options = [{id: '1', user: {name: 'one', email: 'one@gmail.com'}}]
 * formatDataForSelectDropdown(options, {nesting: 'user'}) will return
 * [{email: 'one@gmail.com', name: 'one', value: 'one', label: 'one'}]
 * @param {array} array
 * @param {object} params
 */

const formatDataForSelectDropdown = (array, params = {}) => {
  const {
    name = 'name',
    value = 'name',
    label = 'name',
    nesting = ''
  } = params;
  return array.map((el) => {
    const valueToSpread = nesting ? el[nesting] : el;
    return {
      ...valueToSpread,
      name: valueToSpread[name],
      value: valueToSpread[value],
      label: valueToSpread[label]
    };
  });
};

const formatFlatArrayToSelectDropdown = (array) => {
  if (array.length > 0) {
    return array.map((el) => {
      return {
        name: el,
        value: el,
        label: el
      };
    });
  } else {
    return array;
  }
};

const getIconType = (icon, obj) => {
  switch (icon.type) {
    case 'supericon':
      return (
        <SVGIcon
          name={icon.name}
          color={icon.color ?? 'black'}
          size={icon?.size ?? 'md'}
        />
      );
    case 'svg':
      return (
        <BackgroundIcon
          url={`url(${envConfig.imageRoot}svg/${icon.fileName})`}
        />
      );
    case 'emoji':
      return <Emoji symbol={icon.symbol} label={icon.label} />;
    case 'assetType':
      return (
        <AssetTypeCell
          iconType={icon.variant}
          cell={{ row: { original: obj } }}
        />
      );
    default:
      break;
  }
};

const getCustomDomainUrl = () => {
  const customDomain = localStorage.getItem('apiDomain');

  const excludedDomains = ['superopsalpha.com', 'superops.ai'];

  // Check if the domain contains superopsalpha.com or superops.ai
  if (
    customDomain &&
    excludedDomains.some((domain) => customDomain.includes(domain))
  ) {
    return null;
  }

  return customDomain;
};

const getBaseUrl = (
  idp = false,
  checkDcRegion = false,
  customDomain = false
) => {
  const { isProdEnv, dcRegion } = getRegionAndEnv();
  const subdomain = idp ? 'auth' : 'serv';

  if (customDomain) {
    const customDomainUrl = getCustomDomainUrl();
    if (customDomainUrl) {
      return customDomainUrl.endsWith('/')
        ? customDomainUrl.slice(0, -1)
        : customDomainUrl;
    }
  }

  if (isProdEnv) {
    return checkDcRegion && dcRegion === 'EU'
      ? `https://eu${subdomain}.superops.ai`
      : `https://us${subdomain}.superops.ai`;
  } else {
    return checkDcRegion && dcRegion === 'EU'
      ? `https://eu${subdomain}.superopsalpha.com`
      : `https://us${subdomain}.superopsalpha.com`;
  }
};

const getAiCommunityBmKey = () => {
  const myHeaders = new Headers();
  myHeaders.append('Content-Type', 'application/json');

  const requestOptions = {
    method: 'GET',
    headers: myHeaders,
    redirect: 'follow',
    credentials: 'include'
  };

  return fetch(
    `${getBaseUrl(true, true)}/api/thirdparty/bettermode/aicommunity/key`,
    requestOptions
  )
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
      throw new Error('Failed to fetch ai-community-bm-key');
    })
    .then((result) => {
      localStorage.setItem('ai-community-bm-key', result?.['bm-key']);
    })
    .catch((error) => console.log('ai-community bm key error', error));
};

const getRegionAndEnv = () => {
  const isProdEnv = envConfig.mode.includes('production');
  const dcRegion = localStorage.getItem('dcRegion');

  return {
    isProdEnv: isProdEnv || false,
    dcRegion: dcRegion || 'US'
  };
};

const getRoute = (route, obj) => {
  let processedRoute = '';
  route.accessors.forEach((accessor, index, array) => {
    let separator =
      array.length - 1 === index ? '' : route?.accessorsSeparator || '/';
    processedRoute = `${processedRoute}${obj[accessor]}${separator}`;
  });
  return `${route.baseRoute}${processedRoute}`;
};

const transformViewListData = (configs = {}) => {
  const {
    header,
    list,
    icon,
    route,
    addOption,
    roleToCheck,
    tooltip,
    addIcon = 'Add'
  } = configs;
  const processedList = list.map((el) => {
    return {
      ...el,
      icon:
        el &&
        el.systemViewProperty &&
        TRASH_SYSTEM_VIEW_PROPERTY.includes(el.systemViewProperty)
          ? getIconType(trashIcon, el)
          : getIconType(icon, el),
      route: getRoute(route, el)
    };
  });
  return {
    header,
    list: processedList,
    route,
    addOption,
    roleToCheck,
    tooltip,
    addIcon
  };
};
/**
 * Component to render banner in listing page
 * @param {icon, message} props
 * @returns JSX
 */
const ListInfoBanner = (props) => {
  const { icon, message } = props;
  return (
    <div
      style={{
        backgroundColor: '#FFC20380'
      }}
      className="margin-auto bshadow p10 f-12 list-info-banner"
    >
      <span className="flex actr">
        <div className="fx center" style={{ marginRight: '5px' }}>
          <SVGIcon name={icon || 'InlineError'} size="sm" />
        </div>
        <div className="lh-18">
          <FormattedMessage id={message || 'project.view.trash.information'} />
        </div>
      </span>
    </div>
  );
};

const WithCardPage = (props) => {
  const {
    isNewLayout,
    children,
    height = '84px',
    className = '',
    noContentPadding,
    contentHeight = '100px',
    contentPadding = '20px 40px',
    headerHeight = '60px',
    boxShadow = '0px 2px 25px rgb(0 0 0 / 5%)'
  } = props;
  return isNewLayout ? (
    <CardPage
      {...props}
      height={height}
      className={`${className} p20`}
      headerPadding="20px 40px"
      contentPadding={contentPadding}
      headerHeight={headerHeight}
      noContentPadding={noContentPadding}
      contentHeight={contentHeight}
      boxShadow={boxShadow}
      noMargin
    >
      {children}
    </CardPage>
  ) : (
    children
  );
};

const WithCard = (props) => {
  const { isNewLayout, children, customClassName } = props;
  return !isNewLayout ? (
    <Card padding="sizeSmall">{children}</Card>
  ) : (
    <div className={customClassName || ''}>{children}</div>
  );
};
const getDownloadURL = (params) => {
  const {
    module = 'NOTIFICATION_TEMPLATE_ATTACHMENT',
    details,
    endPoint = 'download'
  } = params;
  if (!!details) {
    return `${localStorage?.getItem('apiDomain') +
      envConfig.fileRoot}/${endPoint}?module=${module}&details=${escape(
      JSON.stringify(details)
    )}`;
  }
  return `${localStorage?.getItem('apiDomain') +
    envConfig.fileRoot}/${endPoint}?module=${module}`;
};
/**
 *
 * @param {object} filterData -> Data returned from tableView on filter set condition
 * @param {Array} tableData -> List of table data
 * @returns
 */
function filterTableDataClientSide(filterData, tableData) {
  let filter = {};
  filterData.map((item) => {
    if (item && item.attribute && item.value) {
      filter = {
        ...filter,
        [item.attribute]: item.value
      };
    }
  });
  const filteredData = tableData.filter((item) => {
    for (let key in filter) {
      let itemValue = item[key];
      // If value of field is an object of the form {actualValue, value, units} filter based on item[key].value
      if (typeof itemValue === 'object') {
        // memory websocket payload is changed and to handle the search, added additional condition
        itemValue =
          itemValue.byteConv !== undefined
            ? itemValue.byteConv
            : itemValue.value;
      }
      if (
        !itemValue ||
        !String(itemValue)
          .toLowerCase()
          .includes(String(filter[key]).toLowerCase())
      )
        return false;
    }
    return true;
  });
  return filteredData;
}

/**
 *
 * @param {array} tableData -> List of table data
 * @param {object} sortAttributes -> Data returned from tableView on table change
 * @returns
 */

function sortTableDataClientSide(
  tableData,
  sortAttributes,
  sortType,
  isNestedObj
) {
  const type = sortType?.type || String;
  const nextState = produce(tableData, (draft) => {
    draft.sort((a, b) => {
      const sortAttributeValue = sortAttributes.sort[0].attribute;
      let currentValue = a[sortAttributeValue]?.value || a[sortAttributeValue];
      let nextValue = b[sortAttributeValue]?.value || b[sortAttributeValue];
      if (isNestedObj) {
        currentValue =
          get(a, sortAttributeValue)?.value || get(a, sortAttributeValue);
        nextValue =
          get(b, sortAttributeValue)?.value || get(b, sortAttributeValue);
      }
      switch (type) {
        case 'Memory':
        case Number:
          return sortAttributes.sort[0].order === 'DESC'
            ? type(nextValue) - currentValue
            : type(currentValue) - nextValue;
        case String:
          // Convert strings to lowercase for case-insensitive comparison
          const lowerCurrentValue = String(currentValue).toLowerCase();
          const lowerNextValue = String(nextValue).toLowerCase();
          return sortAttributes.sort[0].order === 'DESC'
            ? lowerNextValue.localeCompare(lowerCurrentValue)
            : lowerCurrentValue.localeCompare(lowerNextValue);
        default:
          break;
      }
    });
  });

  return nextState;
}

/**
 * This is a helper to provide the equivalent of setState callback in class based components to functional components
 * @param {object} initialState
 * @returns {Array}
 */
function useStateCallback(initialState) {
  const [state, setState] = useState(initialState);
  const cbRef = useRef(null); // init mutable ref container for callbacks

  const setStateCallback = (s, cb) => {
    cbRef.current = cb; // store current, passed callback in ref
    return setState(s);
  }; // keep object reference stable, exactly like `useState`

  useEffect(() => {
    // cb.current is `null` on initial render,
    // so we only invoke callback on state *updates*
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null; // reset callback after execution
    }
  }, [state]);

  return [state, setStateCallback];
}

const stopEvents = (e) => {
  e.stopPropagation();
  e.preventDefault();
};

const stopPropagation = (e) => {
  e.stopPropagation();
};

const isPremiumPlan = (user) => {
  let validPlans = [
    'premium',
    'nfr',
    'ultra',
    'connect',
    'standard-rmm',
    'super',
    'super-2024',
    'standard-rmm-2024'
  ];
  return validPlans.includes(user?.chargeBeePlanId?.toLowerCase());
};

function getBrowserType() {
  var Browser,
    UsrAg = navigator.userAgent;

  if (UsrAg.indexOf('Firefox') > -1) {
    Browser = 'Mozilla Firefox';
  } else if (UsrAg.indexOf('SamsungBrowser') > -1) {
    Browser = 'Samsung Internet';
  } else if (UsrAg.indexOf('Opera') > -1 || UsrAg.indexOf('OPR') > -1) {
    Browser = 'Opera';
  } else if (UsrAg.indexOf('Trident') > -1) {
    Browser = 'Microsoft Internet Explorer';
  } else if (UsrAg.indexOf('Edge') > -1) {
    Browser = 'Microsoft Edge (Legacy)';
  } else if (UsrAg.indexOf('Edg') > -1) {
    Browser = 'Microsoft Edge (Chromium)';
  } else if (UsrAg.indexOf('Chrome') > -1) {
    Browser = 'Google Chrome or Chromium';
  } else if (UsrAg.indexOf('Safari') > -1) {
    Browser = 'Apple Safari';
  } else {
    Browser = 'unknown';
  }
  return Browser;
}

const getHighlightedText = (text, highlight) => {
  if (text) {
    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));
    return parts.map((part, i) => (
      <span
        key={i}
        style={
          part.toLowerCase() === highlight.toLowerCase()
            ? { backgroundColor: 'yellow' }
            : {}
        }
      >
        {part}
      </span>
    ));
  }
  return text; //to handle emply labels
};

const getBrandingLogoAndName = async () => {
  const requestOptions = {
    method: 'GET',
    headers: getHeaderValue(),
    credentials: 'include'
  };
  try {
    const response = await fetchApi(
      `${envConfig.loginRoot}/organization_information`,
      requestOptions
    );
    const data = await response.json();
    return data;
  } catch (err) {
    return {};
  }
};

/**
 * @description
 * This method returns the count for each occurence of an object with a particular key value
 * in an array of objects
 * @param {Array} allItemsArray
 * @returns {Array}
 */
const getObjectValueCount = (allItemsArray, key) => {
  const countArray = allItemsArray.reduce(function(arr, entry) {
    arr[entry.email] = (arr[entry[key]] || 0) + 1;
    return arr;
  }, {});

  return countArray;
};

function useActivityTracker(args) {
  const productType = localStorage.getItem('productType');
  if (window.mixpanel) {
    let trackerData = {
      hash: window.location.hash,
      productType
    };
    if (args.data) {
      trackerData = {
        ...trackerData,
        ...args.data
      };
    }
    if (window.mixpanel?.track) window.mixpanel.track(args.event, trackerData);
    // datadogRum.addAction(args.event, trackerData);
  }
  // When we remove mixpanel, uncomment this code

  // let trackerData = {
  //   hash: window.location.hash
  // };
  // if (args.data) {
  //   trackerData = {
  //     ...trackerData,
  //     ...args.data
  //   };
  // }
  // datadogRum.addAction(args.event, trackerData);
  return true;
}

function useUserMSPCheckHook() {
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const isMSP = currentUser?.accountType === 'MSP';
  return isMSP;
}

export function useUserClientCheck() {
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  return currentUser?.accountType === 'CLIENT';
}

function useIsAuthorisedHook(role) {
  const appContext = useContext(AppContext);
  const user = appContext?.user || {};
  const roleFeatures = user?.role?.roleFeatureList || [];
  const isRoleAuthorised = isAuthorised(roleFeatures, role);
  return isRoleAuthorised;
}

function useProduct(productToCheck) {
  const appContext = useContext(AppContext);
  const currentProduct = appContext?.currentProduct;

  // To get the currentProduct value instead of boolean value, do not pass 'productToCheck'
  if (!productToCheck) {
    return currentProduct;
  }
  // incase in future if we need to check for multiple products, pass the products key in array format
  if (typeof productToCheck === 'object') {
    return productToCheck.some((p) => p.includes(currentProduct));
  }
  return currentProduct === productToCheck;
}

const useCheckIsItProduct = () => !!useProduct(COMMON_CONSTANTS.IT);

function getTabPersonalisationCategory(currentServicePortal, currentProduct) {
  // tab personalization for IT requester portal, applies to only IT product.
  if (
    currentServicePortal === COMMON_CONSTANTS.REQUESTER &&
    currentProduct === COMMON_CONSTANTS.IT
  ) {
    return `tabsPersonalization_${COMMON_CONSTANTS.REQUESTER}`;
  }
  return 'tabsPersonalization';
}

function useCurrentServicePortal(categoryName) {
  const appContext = useContext(AppContext);
  const { currentProduct, currentServicePortal } = appContext;
  if (
    currentServicePortal === COMMON_CONSTANTS.REQUESTER &&
    currentProduct === COMMON_CONSTANTS.IT
  ) {
    return `${categoryName}_${COMMON_CONSTANTS.REQUESTER}`;
  }
  return categoryName;
}

function useUserMSPCheckHookBasedOnProduct() {
  const appContext = useContext(AppContext);
  const { currentProduct, currentServicePortal } = appContext;
  if (currentProduct === COMMON_CONSTANTS.IT) {
    const isITTechnician =
      currentProduct === COMMON_CONSTANTS.IT &&
      currentServicePortal === COMMON_CONSTANTS.TECHNICIAN;
    return isITTechnician;
  } else {
    return useUserMSPCheckHook();
  }
}

function useCurrentLanguage() {
  const appContext = useContext(AppContext);
  const lang = appContext?.currentLanguage?.value || {};
  return lang;
}

function useTranslatedMessages() {
  const appContext = useContext(AppContext);
  const lang = appContext?.languageData || {};
  return lang;
}

function useIntl() {
  const currentLanguage = 'en'; // useCurrentLanguage();
  const translationMessages = window.TranslationMessages; // useTranslatedMessages();
  const intl = createIntl({
    locale: currentLanguage,
    messages: translationMessages
  });
  return intl;
}
function useSiteListForDropdown(
  clientId,
  queryAllSitesDetails = '',
  ignoreAccountInSite = false
) {
  const query = queryAllSitesDetails
    ? GET_CLIENT_SITE_LIST_WITH_INSTALLER_INFO
    : ignoreAccountInSite
    ? GET_CLIENT_SITE_LIST_FOR_DROPDOWN_WO_ACCOUNT
    : GET_CLIENT_SITE_LIST_FOR_DROPDOWN;
  const [sites, setSites] = useState(null);
  const [extraVariables, setExtraVariables] = useState({});
  const [getClientSiteList, { loading, error, data }] = useLazyQuery(query, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });
  function getClientSites(client, anyExtraVariables) {
    getClientSiteList({
      variables: getClientSiteVariablesById(client)
    });
    setExtraVariables(anyExtraVariables);
  }
  useEffect(() => {
    if (clientId) {
      getClientSiteList({
        variables: getClientSiteVariablesById(clientId)
      });
    }
  }, [clientId]);

  useEffect(() => {
    if (data?.getClientSiteList?.sites) {
      setSites(data?.getClientSiteList?.sites);
    }
  }, [data]);

  return {
    loading,
    error,
    data: sites,
    getClientSites,
    extraVariables
  };
}

function rearrangeArrayValue(array, currentIndex, targetIndex) {
  return array.splice(targetIndex, 0, array.splice(currentIndex, 1)[0]);
}

const getCompanyInfoHook = () => {
  const appContext = useContext(AppContext);
  return appContext?.companyInfo;
};

const getNotificationTemplateByConstantHook = (variables) => {
  // if (variables) {
  //   const isNotificationTemplateAuthorised = useIsAuthorisedHook(
  //     'ticketNotificationTemplate.view'
  //   );

  //   const { loading, error, data } = useQuery(
  //     GET_NOTIFICATION_TEMPLATE_BY_CONSTANT,
  //     {
  //       client: AccountClient,
  //       variables: variables,
  //       fetchPolicy: 'cache-and-network',
  //       skip: !isNotificationTemplateAuthorised
  //     }
  //   );

  //   if (data) {
  //     return data?.getNotificationTemplateByConstant;
  //   }
  // }
  return {
    id: '1004',
    activated: true
  };
};

function getTechnicianCount() {
  const appContext = useContext(AppContext);
  const techCount =
    appContext?.customerLicense?.data?.getCustomerLicenseExceededDetails
      ?.isTechnicianLicenseCountExceeded;
  return techCount;
}

function getCurrentUserHook() {
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  return currentUser;
}

function getIntegratedCalendarHook() {
  const appContext = useContext(AppContext);
  const integratedCalenderData = appContext?.integratedCalenderData || {};
  return integratedCalenderData;
}

function getDifference(target, base) {
  const difference = {};
  Object.keys(target)?.forEach((prop) => {
    if (!isEqual(target?.[prop], base?.[prop])) {
      difference[prop] = target[prop];
    }
  });
  return difference;
}

function openTabHook({ module, ...restData }) {
  const tabContext = useContext(TabContext);
  return ({ url, ...rest }) => {
    if (tabContext?.openTab) {
      tabContext.openTab({
        module: module,
        ...restData,
        url: url,
        ...rest
      });
    }
  };
}

function withForwardedRef(Comp) {
  const WrappedComp = React.forwardRef((props, ref) => (
    <Comp {...props} forwardedRef={ref} />
  ));
  WrappedComp.displayName = `withForwardedRef-${Comp.displayName ?? Comp.name}`;
  return WrappedComp;
}
const formatCustomFieldForDropDown = (array) => {
  return (
    array &&
    array.map((el) => ({
      ...el,
      name: el?.value || el?.value,
      value: el?.value,
      label: el?.value
    }))
  );
};

const getDateTimeFormat = () => {
  const context = useContext(AppContext);
  const dateFormat = context?.companyInfo?.dateFormat;
  const MSPtimeFormat = context?.companyInfo?.timeFormat;

  const MSPDateFormat =
    DATE_FORMAT_FOR_DATE_PICKER?.[dateFormat] || 'MMM dd, yyyy';

  let dateTimeFormat = null;
  const defaultDateTimeFormat = 'MMMM d, yyyy hh:mm aa';

  let timeFormat = null;
  const defaultTimeFormat = 'hh:mm aa';

  if (MSPtimeFormat === 'HH:mm') {
    // 24 hrs
    timeFormat = 'HH:mm';
    dateTimeFormat = `${MSPDateFormat} HH:mm`;
  } else {
    // 12hrs
    timeFormat = 'hh:mm aa';
    dateTimeFormat = `${MSPDateFormat} hh:mm aa`;
  }

  return {
    dateTimeFormat: dateTimeFormat || defaultDateTimeFormat, // Gives DateTimeFormat for React date picker
    dateFormat: MSPDateFormat, // Gives dateFormat for React date picker
    MSPDateFormat: dateFormat, // Gives dateFormat from API
    timeFormat: timeFormat, // Gives timeFormat for React date picker
    MSPtimeFormat: MSPtimeFormat || defaultTimeFormat, //  Gives timeFormat from API
    timeType: MSPtimeFormat === 'HH:mm' ? '24Hours' : '12Hours'
  };
};

const getVariablesForSelectionInput = (records, dataKey, key, variableKey) => {
  // dataKey - key in which the necessary id is present
  // key - key in which the item needs to be returned
  //  variableKey: variable in which we need data
  if (records?.allSelected) {
    return {
      selectionType: 'ALL',
      filterCondition: records.filterCondition
    };
  } else if (
    records?.unSelectedRecords &&
    records?.unSelectedRecords.length > 0
  ) {
    const items = records?.unSelectedRecords.map((el) => ({
      [key]: el?.[dataKey]
    }));
    return {
      selectionType: 'UNSELECTED',
      [variableKey]: items,
      filterCondition: records.filterCondition
    };
  } else if (records?.selectedRecords && records?.selectedRecords.length > 0) {
    const items = records?.selectedRecords.map((el) => ({
      [key]: el?.[dataKey]
    }));
    return {
      selectionType: 'SELECTED',
      [variableKey]: items,
      filterCondition: records.filterCondition
    };
  }
};
const checkForMobileOrTabBrowser = () => {
  let check = false;
  (function(a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

function calculateTermAndDueDate(invoiceDate, termType, termValue) {
  let dueDate = null;
  const currentDate = dayjs(invoiceDate).date();
  const noOfDaysInCurrentMonth = dayjs(invoiceDate).daysInMonth();
  const noOfDaysInNextMonth = dayjs(invoiceDate)
    .add(1, 'M')
    .daysInMonth();

  switch (termType) {
    case 'placeholder.due.on.receipt':
      dueDate = dayjs(invoiceDate).toDate();
      break;
    case 'placeholder.due.end.this.month':
      dueDate = dayjs(invoiceDate)
        .add(noOfDaysInCurrentMonth - currentDate, 'day')
        .toDate();
      break;
    case 'placeholder.due.end.next.month':
      dueDate = dayjs(invoiceDate)
        .add(noOfDaysInCurrentMonth - currentDate + noOfDaysInNextMonth, 'day')
        .toDate();
      break;
    default:
      dueDate = dayjs(invoiceDate)
        .add(termValue, 'day')
        .toDate();
      break;
  }

  return dayjs(dueDate).format('YYYY-MM-DD');
}

// Helper function to format formTemplate for formBuilder to remove persisted values
const formatFormTemplate = (fieldDetails, stringify) => {
  const emptyFromTemplate = { layout: [] };
  let formTemplate = emptyFromTemplate;
  if (fieldDetails) {
    if (typeof fieldDetails === 'string') {
      formTemplate = JSON.parse(fieldDetails);
    } else if (typeof fieldDetails === 'object') {
      formTemplate = fieldDetails;
    }
  }
  const formLayout = cloneDeep(emptyFromTemplate);
  if (formTemplate?.layout && formTemplate?.layout.length) {
    formTemplate.layout.forEach((el) => {
      const singleFieldData = { ...el };
      if (singleFieldData?.field?.fieldProps) {
        singleFieldData.field.fieldProps.selectedValue = null;
      }
      formLayout?.layout.push(singleFieldData);
    });
  }
  if (stringify) {
    return JSON.stringify(formTemplate);
  } else {
    return formTemplate;
  }
};

function getSourceIcon(source) {
  for (const [key, value] of Object.entries(TICKET_SOURCE)) {
    if (value === source) {
      return key;
    }
  }
}

//This helper is for constructing and combining formMeta json and custom fields for asset classes
const formateGridDataForAssetClass = async ({ formMeta, customFields }) => {
  //considering all default fields and data inside customFields json
  //This will filter out data which is present in form meta but not in customfield
  let transformedFormMeta = JSON.parse(formMeta)['layout'].filter((item) => {
    if (item.field.type === 'assetDefault') {
      return true;
    } else {
      return (
        item?.field?.fieldProps?.id ===
        customFields.find(
          (innerItem) => innerItem.id === item?.field?.fieldProps?.id
        )?.['id']
      );
    }
  });

  //Getting the last grid position to append missing custom fields in form meta
  const dimensions = await getDimensions({
    layout: transformedFormMeta
  });

  let yAxis = dimensions.y;
  let existingIdsMapForFiltering = {};

  //just a object map for filtering purpose below
  transformedFormMeta.forEach((item) => {
    if (item?.field?.fieldProps?.id) {
      existingIdsMapForFiltering = {
        ...existingIdsMapForFiltering,
        [item?.field?.fieldProps?.id]: item?.field?.fieldProps?.id
      };
    }
  });

  //if there are any missing fields which are not present in form meta and present in
  //custom fields, those fields will be append at the end of the form meta manually
  const gridLayout = [
    ...transformedFormMeta,
    ...customFields
      .filter((item) => !existingIdsMapForFiltering[item.id])
      .map((item, index) => {
        yAxis = index === 0 ? yAxis : yAxis + 1;
        return {
          field: {
            type: 'custom',
            fieldProps: {
              ...item
            },
            conditions: null
          },
          h: 2,
          w: 1,
          x: 0,
          y: yAxis,
          i: `${nanoid()}`
        };
      })
  ];
  return gridLayout;
};

function getWeekOfYear(year, week) {
  // The first monday of the first week includes at least one day of the year
  let firstMondayOfYear = dayjs()
    .year(year)
    .isoWeek(week)
    .day(1);
  // Now make sure it really is the first monday of the year
  if (firstMondayOfYear.year() !== year) {
    firstMondayOfYear = firstMondayOfYear.add(7, 'days');
  }
  return new Array(7)
    .fill(firstMondayOfYear)
    .map((day, idx) => day.add(idx, 'day').format('YYYY-MM-DD'));
}

function getWeekOfDate(date) {
  const dateInConsideration = date ? dayjs(date) : dayjs();

  // Monday of the week which the date lies in
  let firstMondayOfWeek = dateInConsideration.startOf('isoWeek');

  return new Array(7)
    .fill(firstMondayOfWeek)
    .map((day, idx) => day.add(idx, 'day').format('YYYY-MM-DD'));
}

export function sanitizeWhiteSpace(str) {
  if (str) {
    return str.replace(/[\u200B-\u200D\uFEFF]/g, '');
  }

  return str;
}

export function roundToFixed(num, digits) {
  const floatNumber = parseFloat(num);
  const number = new BigNumber(floatNumber);
  return number.decimalPlaces(digits, BigNumber.ROUND_HALF_UP).toNumber();
}

const sanitizeString = (html) => {
  let sanitizedString = html;
  // const regex = /[\u{0080}-\u{FFFF}]/gu;

  // const found = sanitizedString.match(regex);
  // if (found) {
  // sanitizedString = sanitizedString.replace(/[\u{0080}-\u{FFFF}]/gu, '');
  sanitizedString = sanitizedString.replace(/[\u200B-\u200D\uFEFF]/g, '');
  // }
  return sanitizedString;
};

const getPositiveValue = (input) => {
  // Check if value is falsy
  if (!input || isNaN(input)) {
    return '0';
  }

  const isDecimal = input.includes('.');

  // Check for integers and remove leading zeroes leading zeros
  if (!isDecimal) {
    let parsedInt = parseInt(input, 10).toString();
    parsedInt = parsedInt.replaceAll('-', '').trim();
    return parsedInt;
  }

  // Check for decimal
  if (isDecimal) {
    const parts = input.split('.');
    // Remove leading 0 in integer part
    const integer = parseInt(parts[0], 10);
    const integerPart = !isNaN(integer) ? Math.abs(integer) : 0;
    // Remove negative value from integer part
    const decimalPart = parts[1];
    return `${integerPart}.${decimalPart}`;
  }

  // Fallback,
  // 1. replace - with ''
  // 2. trim input
  return input.replaceAll('-', '').trim();
};

function convertToMSPTimeZone(dateTime, isDateOnly = false) {
  const context = useContext(AppContext);
  const { dateFormat, timeFormat, timezoneCode } = context?.companyInfo || {};

  return dayjs(dateTime || new Date())
    .utc()
    .tz(timezoneCode)
    .format(isDateOnly ? DATE_FORMAT : DATE_TIME_FORMAT);
}

export function downloadHack({ href, downloadFileName, target = null }) {
  const isFirefox = /firefox/i.test(navigator.userAgent);
  const a = document.createElement('a');
  a.href = href;
  a.download = downloadFileName;
  a.target = target || isFirefox ? '_blank' : '_self';
  if (isFirefox) {
    a.rel = 'noopener noreferrer';
  }
  a.click();
}

const translateText = (lokaliseKey, values) => {
  const intl = useReactIntl();
  if (lokaliseKey) {
    return intl.formatMessage({ id: lokaliseKey }, values);
  }
  return null;
};

export function RenderTooltip({ isVisible, children, tooltip }) {
  if (isVisible) {
    return React.cloneElement(tooltip, { children });
  }
  return children;
}

export function Error({ children }) {
  return children && reactSelectInlineError(children);
}

function getPortalCustomDomain(domains = []) {
  // returns cname portal url
  return domains.find((d) => d.type === 'CUSTOM_DOMAIN' && d.isDefault) || {};
}
function getPortalCustomSubDomain(domains = []) {
  // return the subdomain <subdomain.domain.com>
  return (
    domains.find((d) => d.type === 'CUSTOM_SUBDOMAIN') ||
    domains.find((d) => d.type === 'SYSTEM_SUBDOMAIN') ||
    {}
  );
}

export function getAIPrompts() {
  const aiFeatureHook = AIFeaturesHook();
  const promptBuilderData = aiFeatureHook.getAIFeatureData('PROMPT_BUILDER');
  const prompts = promptBuilderData?.subFeatures?.filter(
    (el) => el?.state === 'ACTIVE'
  );

  return prompts || [];
}

const getTabModule = (url, allUrl) => {
  function generateRegExpPattern(dynamicPath) {
    const escapedDynamicPath = dynamicPath.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    );
    const pattern = escapedDynamicPath.replace(/:\w+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex;
  }

  let moduleName;
  const data = {};
  let pathElem;
  let pathElemCount = 0;

  const urlParams = url.split('/');
  urlParams.splice(0, 1);

  if (
    (urlParams?.[0] === 'ticket' || urlParams?.[0] === 'tasks') &&
    !Number.isNaN(Number(urlParams?.[1]))
  ) {
    return { module: 'WORKSPACE', data: data };
  }

  for (let key in allUrl) {
    let urlRegex;
    if (Array.isArray(allUrl[key].path)) {
      allUrl[key].path.forEach((elem, idx) => {
        urlRegex = generateRegExpPattern(elem);
        if (urlRegex.test(url)) {
          if (pathElemCount < 1) {
            moduleName = key;
            pathElem = elem;
            pathElemCount = 1;
          }
        }
      });
    } else {
      urlRegex = generateRegExpPattern(allUrl[key].path);
      if (urlRegex.test(url)) {
        if (pathElemCount < 1) {
          moduleName = key;
          pathElem = allUrl[key].path;
          pathElemCount = 1;
        }
      }
    }
  }

  if (!pathElem) {
    return { module: 'NOTFOUND', data: data };
  }
  const path = pathElem.split('/');
  const values = url.split('/');
  // console.log(path, values);

  for (let i = 0; i < path.length; i++) {
    if (path[i].startsWith(':')) {
      data[path[i].substring(1)] = values[i];
    }
  }

  return { module: moduleName, data: data };
};

function restructureHtml(html) {
  const container = document.createElement('div');
  container.innerHTML = html;

  const spans = container.querySelectorAll(
    'span.fr-deletable.fr-tribute.mention'
  );

  spans.forEach((span) => {
    const placeholderText = span.textContent.trim().replace(/^#/, '');

    span.setAttribute('contenteditable', 'false');
    span.innerHTML = `
      <span class="ql-mention-denotation-char">#</span>${placeholderText}
    `;
  });

  return container.innerHTML.replace(/\s*\n\s*/g, '').trim();
}

const formatContentWithMentionsAndPlaceholders = (htmlString) => {
  // When we get the model with mentions and placeholders,
  // We'll remove the inline styles and add the classes 'fr-deletable', 'fr-tribute', and 'mention'
  // Parse the HTML string into a DOM structure
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Select all elements with data-denotation-char="@" or data-denotation-char="#"
  const elements = doc.querySelectorAll(
    '[data-denotation-char="@"], [data-denotation-char="#"]'
  );

  // Loop through each selected element
  elements.forEach((element) => {
    // Remove the inline style
    element.removeAttribute('style');
    // Add the classes 'fr-deletable', 'fr-tribute', and 'mention'
    element.classList.add('fr-deletable', 'fr-tribute', 'mention');
  });

  // Return the modified HTML as a string
  return doc.body.innerHTML;
};

const EventDispatcher = {
  events: {},
  subscribe: function subscribe(eventName, callback) {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }

    this.events[eventName].push(callback);

    return () => {
      this.events[eventName] = this.events[eventName].filter(
        (cb) => cb !== callback
      );
    };
  },
  dispatch: function dispatch(eventName, ...args) {
    this.events[eventName]?.forEach((cb) => cb(...args));
  }
};

const convertTo12HoursFormat = (timeString) => {
  let [hours, minutes] = timeString.split(':').map(Number);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    // Validation for proper time input
    throw new Error('Invalid time format');
  }

  const period = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  hours = String(hours).padStart(2, '0');
  minutes = String(minutes).padStart(2, '0');

  return `${hours}:${minutes} ${period}`;
};

function getConversationOrder() {
  const appContext = useContext(AppContext);
  const conversationOrder = appContext?.conversationOrder || {};
  return conversationOrder;
}

const scrollToBottom = (ref) => {
  if (ref?.current) {
    ref.current.scrollTop = ref.current?.scrollHeight;
  }
};
const scrollToTop = (ref) => {
  if (ref?.current) {
    ref.current.scrollTop = 0;
  }
};

const reactbellchatScrollBottom = (ref) => {
  if (ref?.current) {
    // scrolledToBottom will move the scroll to bottom automatically in the react-bell-chat library
    let scrollHeightVal = ref.current?.scrollApi?.scrollHeight();
    let clientHeightVal = ref.current?.scrollApi?.clientHeight();
    ref.current?.scrollApi?.scrollTo(scrollHeightVal - clientHeightVal);
  }
};
const reactbellchatScrollTop = (ref) => {
  if (ref.current) {
    // scrollTo(0) will move the scroll to top automatically in the react-bell-chat library
    ref.current?.scrollApi?.scrollTo(0);
  }
};

const getModulePathName = (item, defaultModule) => {
  let module = defaultModule,
    pathname = '';

  if (item?.name?.split('.')?.length > 1) {
    module = item.name.split('.').shift();
    pathname = item.name.split('.').pop();
  } else {
    pathname = item.name;
  }
  return { module, pathname };
};

const convertMarkdownToHtml = (markdown, options = {}) => {
  const defaultOptions = {
    html: true,
    linkify: true,
    typographer: true
  };

  const md = markdownit({
    ...defaultOptions,
    ...options
  });

  return md.render(markdown);
};

const getHtmlFromMarkDown = (markdown, options = {}) => {
  const defaultOptions = {
    html: true,
    linkify: true,
    typographer: true
  };

  const md = markdownit({
    ...defaultOptions,
    ...options
  });

  md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const aIndex = tokens[idx].attrIndex('target');
    if (aIndex < 0) {
      tokens[idx].attrPush(['target', '_blank']); // Add target="_blank" if not present
    }
    return self.renderToken(tokens, idx, options);
  };

  const htmlText = md.render(markdown);
  return htmlText;
};

function getWindowId() {
  const appContext = useContext(AppContext);
  return appContext?.windowId?.current || null;
}

function usePersonalisedState(initialState, { name, category }) {
  const user = getCurrentUserHook();
  const [personalisedState, setPersonalisedState] = useState(initialState);
  const [loading, setLoading] = useState(true);

  function getPersonalisedState() {
    setLoading(true);
    AccountClient?.query({
      query: GET_PERSONALIZATION_BY_NAME,
      fetchPolicy: 'cache-first',
      variables: { userId: user?.userId, category, name }
    })
      .then((res) => {
        if (
          res.data.getPersonalizationByCategoryAndName &&
          'value' in res.data.getPersonalizationByCategoryAndName
        ) {
          setPersonalisedState(
            JSON.parse(res.data.getPersonalizationByCategoryAndName?.value)
          );
        } else {
          setPersonalisedState(initialState);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }

  useEffect(() => {
    if (user?.userId) {
      getPersonalisedState();
    }
  }, [user?.userId, name, category]);

  const savePersonalisedState = (updatedState) => {
    if (user?.userId) {
      const currentPersonalisedState = cloneDeep(personalisedState);
      setPersonalisedState(updatedState);
      AccountClient?.mutate({
        mutation: SAVE_PERSONALIZATION,
        variables: {
          user: {
            userId: user.userId
          },
          category,
          name,
          value: JSON.stringify(updatedState)
        }
      })
        .then(() => {
          const data = AccountClient.readQuery({
            query: GET_PERSONALIZATION_BY_NAME,
            variables: { userId: user?.userId, category, name }
          });
          console.log(data);
          AccountClient.writeQuery({
            query: GET_PERSONALIZATION_BY_NAME,
            variables: { userId: user?.userId, category, name },
            data: {
              getPersonalizationByCategoryAndName: {
                ...data?.getPersonalizationByCategoryAndName,
                value: JSON.stringify(updatedState)
              }
            }
          });
        })
        .catch((err) => {
          console.error(err);
          setPersonalisedState(currentPersonalisedState);
        });
    }
  };

  return [personalisedState, savePersonalisedState, loading];
}

function getValuesWithDecimalPlaces(value, maxDecimalPlaces = 3) {
  return value?.toString()?.includes('.') &&
    value?.toString()?.split('.')[1]?.length > maxDecimalPlaces
    ? value.toFixed(maxDecimalPlaces)
    : value;
}
const getButtonVariant = ({ variant, defaultVariant }) => {
  switch (variant) {
    case 'primary':
      return {
        primary: true
      };
    case 'secondary':
      return {
        secondary: true
      };
    case 'aiPrimary':
      return {
        aiPrimary: true
      };
    case 'aiPrimaryLight':
      return {
        aiPrimaryLight: true
      };
    case 'plainSecondary':
      return {
        plainSecondary: true
      };
    case 'plain':
      return {
        plain: true
      };
    case 'default':
      return {};
    default:
      if (defaultVariant) {
        return {
          [defaultVariant]: true
        };
      }
      return {};
  }
};

export {
  getModulePathName,
  getConversationOrder,
  scrollToBottom,
  scrollToTop,
  reactbellchatScrollBottom,
  reactbellchatScrollTop,
  getPortalCustomDomain,
  getPortalCustomSubDomain,
  getTranslatedText,
  getURLSearchParams,
  translateText,
  getParsedCrudData,
  isAuthorised,
  isProductAuthorised,
  useProduct,
  sendMessageToContentJS,
  EventEmitter,
  EventEmitterWithIndex,
  getErrorData,
  getErrorTexts,
  getFormattedDateTime,
  generateString,
  getDownloadURL,
  toastMessage as toast,
  animatedToast,
  getCookieValue,
  actionToastMessage as actionToast,
  getEntityAttributes,
  findLastIndex,
  formatDataForSelectDropdown,
  transformViewListData,
  ListInfoBanner,
  getHeaderValue,
  getIdpHeaders,
  getCustomSubDomain,
  WithCard,
  WithCardPage,
  filterTableDataClientSide,
  sortTableDataClientSide,
  useStateCallback,
  stopEvents,
  stopPropagation,
  isPremiumPlan,
  getBrowserType,
  getHighlightedText,
  getBrandingLogoAndName,
  getObjectValueCount,
  getToastMessage,
  getDisplayName,
  useUserMSPCheckHook,
  useIsAuthorisedHook,
  rearrangeArrayValue,
  formatFlatArrayToSelectDropdown,
  useIntl,
  useCurrentLanguage,
  useTranslatedMessages,
  useActivityTracker,
  planName,
  getCompanyInfoHook,
  getCurrentUserHook,
  getDifference,
  withForwardedRef,
  openTabHook,
  formatCustomFieldForDropDown,
  getTechnicianCount,
  getDateTimeFormat,
  getVariablesForSelectionInput,
  checkForMobileOrTabBrowser,
  calculateTermAndDueDate,
  formatFormTemplate,
  getSourceIcon,
  formateGridDataForAssetClass,
  getWeekOfYear,
  getWeekOfDate,
  sanitizeString,
  isFeatureAuthorisedInPlan,
  dompurifier,
  getNotificationTemplateByConstantHook,
  getPositiveValue,
  convertToMSPTimeZone,
  useSiteListForDropdown,
  getTabModule,
  getTabPersonalisationCategory,
  useCurrentServicePortal,
  formatContentWithMentionsAndPlaceholders,
  getIntegratedCalendarHook,
  restructureHtml,
  EventDispatcher,
  useCheckIsItProduct,
  convertTo12HoursFormat,
  useUserMSPCheckHookBasedOnProduct,
  getWindowId,
  convertMarkdownToHtml,
  usePersonalisedState,
  getValuesWithDecimalPlaces,
  getHtmlFromMarkDown,
  getButtonVariant,
  getBaseUrl,
  getRegionAndEnv,
  getCustomDomainUrl,
  getAiCommunityBmKey
};
